import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { getPortalSupabase } from "@/lib/portalSupabase";
import { Building2, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PortalCallback() {
  const [, setLocation] = useLocation();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");

  useEffect(() => {
    (async () => {
      const search = new URLSearchParams(window.location.search);
      const hash   = new URLSearchParams(window.location.hash.slice(1));

      // Capture URL params upfront for debug display
      const urlParams = {
        code:         !!search.get("code"),
        token_hash:   !!search.get("token_hash"),
        type:         search.get("type"),
        access_token: !!hash.get("access_token"),
        error:        search.get("error") || hash.get("error"),
        error_desc:   search.get("error_description") || hash.get("error_description"),
      };
      setDebugInfo(JSON.stringify(urlParams));

      // If Supabase itself returned an error in the URL, surface it immediately
      if (urlParams.error) {
        setErrorMsg(`Supabase error: ${urlParams.error} — ${urlParams.error_desc || "no description"}`);
        return;
      }

      try {
        const supabase = await getPortalSupabase();

        const code         = search.get("code");
        const tokenHash    = search.get("token_hash");
        const type         = search.get("type");
        const accessToken  = hash.get("access_token");
        const refreshToken = hash.get("refresh_token");

        if (code) {
          // PKCE code from client-initiated OTP
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            // May fail for admin-generated links (no PKCE verifier). Fall through to getSession.
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error(`PKCE exchange failed: ${error.message}`);
          }
        } else if (accessToken && refreshToken) {
          // Implicit flow — admin-generated action_link redirected here with tokens in hash
          const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
          if (error) throw new Error(`setSession failed: ${error.message}`);
        } else if (tokenHash && type) {
          // Direct token_hash URL — invite email link or dev bypass link
          const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: type as any });
          if (error) {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error(`verifyOtp failed: ${error.message}`);
          }
        } else {
          // No URL params — check for an existing valid session
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error) throw new Error(`getSession failed: ${error.message}`);
          if (!session) throw new Error("No auth token found in URL and no existing session");
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Session is null after auth exchange — check Supabase URL allowlist");

        // Accept the invite: links supabase_user_id to invite rows + ensures client role exists
        const acceptRes = await fetch("/api/portal/auth/accept", {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!acceptRes.ok) {
          const body = await acceptRes.json().catch(() => ({}));
          // 403 = no invite row for this email. Not fatal — accounts check below will catch it.
          if (acceptRes.status !== 403) {
            throw new Error(`accept failed ${acceptRes.status}: ${(body as any).message}`);
          }
        }

        // Resolve accounts
        const accountsRes = await fetch("/api/portal/accounts", {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!accountsRes.ok) {
          const body = await accountsRes.json().catch(() => ({}));
          throw new Error(
            accountsRes.status === 403
              ? "Your email doesn't have portal access. Ask your accountant to send you an invite link."
              : `accounts failed ${accountsRes.status}: ${(body as any).message}`
          );
        }

        const accounts: Array<{ account_id: string; account_name: string }> = await accountsRes.json();
        if (accounts.length === 0) {
          throw new Error("No portal accounts found for this email. Ask your accountant to send you an invite link.");
        }

        // Honour deep-link returnTo if present and safe (must start with /portal/)
        const returnTo = search.get("returnTo");
        const destination = (returnTo && returnTo.startsWith("/portal/")) ? returnTo : "/portal";

        if (accounts.length === 1) {
          localStorage.setItem("portal_account_id", accounts[0].account_id);
          setLocation(destination);
        } else {
          localStorage.setItem("portal_multi_account", "true");
          // Preserve returnTo through the account picker (localStorage survives new tabs / mobile backgrounding)
          localStorage.setItem("portal_return_to", destination);
          setLocation("/portal/pick-account");
        }
      } catch (err: any) {
        console.error("Portal callback error:", err);
        setErrorMsg(err?.message || String(err));
      }
    })();
  }, [setLocation]);

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="w-6 h-6 text-primary" />
              <span className="font-semibold text-lg">Carpenter &amp; Company, CPAs</span>
            </div>
            <p className="text-muted-foreground text-sm">Client Portal</p>
          </div>
          <div className="bg-card border rounded-lg p-5 space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-sm">Sign-in failed</p>
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
              </div>
            </div>
            {import.meta.env.DEV && (
              <p className="text-xs text-muted-foreground font-mono bg-muted rounded px-2 py-1 break-all">
                URL params: {debugInfo}
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setLocation("/portal/login")} className="flex-1">
                Back to sign-in
              </Button>
              <Button size="sm" onClick={() => { window.location.href = '/portal/login'; }} className="flex-1">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Try again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Signing you in…</span>
      </div>
    </div>
  );
}
